import type { ReportRange } from '@hbs-us/contracts';

/**
 * Typed search params for the `/reports` route (Discretion #1). The filter lives
 * in the URL so it is deep-linkable AND compile-checked — a mistyped `range`
 * fails the TS build, and TanStack Router validates the raw URL params through
 * `validateReportsSearch` on every navigation.
 *
 * `start`/`end` are only meaningful when `range === 'custom'`; for the preset
 * ranges the server derives the window, so we drop them.
 */

export interface ReportsSearch {
  range: ReportRange;
  start?: string;
  end?: string;
}

const VALID_RANGES: readonly ReportRange[] = ['1w', '1m', '1y', 'custom'];

/** Default to "This Month" — matches the server's default (`reportFilterFromQuery`). */
export const DEFAULT_RANGE: ReportRange = '1m';

function isReportRange(value: unknown): value is ReportRange {
  return typeof value === 'string' && (VALID_RANGES as readonly string[]).includes(value);
}

/** ISO `YYYY-MM-DD` shape guard (the server applies the authoritative validation). */
function asIsoDate(value: unknown): string | undefined {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/**
 * TanStack Router `validateSearch` for the reports route. Coerces the raw URL
 * record into the typed `ReportsSearch`, falling back to the default range when
 * the param is absent or invalid. `start`/`end` are only kept for `custom`.
 */
export function validateReportsSearch(raw: Record<string, unknown>): ReportsSearch {
  const range = isReportRange(raw.range) ? raw.range : DEFAULT_RANGE;
  if (range !== 'custom') {
    return { range };
  }
  return {
    range,
    start: asIsoDate(raw.start),
    end: asIsoDate(raw.end),
  };
}
